<script setup lang="tsx">
import { ref, computed, nextTick, onBeforeMount, watch } from 'vue'
import html2canvas from 'html2canvas'
import { PDFDocument, rgb, type PDFImage, type PDFPage } from 'pdf-lib'
import Modal from '../shared/Modal.vue'
import Overview from './overview/Overview.vue'
import { useI18n } from 'vue-i18n'
import { formatDate, getTreatmentText, hasPermission } from '@/services/utils'
import { PermOwnRiskRead, PermAllRiskRead } from '@/models/enums-generated'
import type { Response, RiskCatalogMeta, PageRequest, Risk, PageResponse, RiskMeta } from '@/models/api-generated'
import type { OrderByField, RiskFilter } from '@/models/filters-generated'
import http from '@/services/http'
import { riskConfig } from '../risk_management/riskConfig'
import { formatAxisLevel, formatRiskScore, formatRiskScoreStr, scoreToLevel } from '../risk_management/shared'
import {
	A4_WIDTH,
	A4_HEIGHT,
	MARGIN,
	CONTENT_START_Y,
	COLORS,
	SPACING,
	TITLE_PAGE,
	sanitizeText,
	wrapText,
	drawFooter,
	drawModernHeader,
	drawSeparator,
	ensureSpace,
	initializeFonts,
	getTitleFont,
	getBodyFont,
	getStatusColor,
	drawText,
	hexToRgb,
	type DrawContext,
} from './utils'
import { loadAllImages, type LoadedImages } from './images'

interface Props {
	overviewRef?: InstanceType<typeof Overview>
	overviewSection?: HTMLElement
	matrixSection?: HTMLElement
	riskScoreSection?: HTMLElement
	filterValue: () => RiskFilter
	orderValue: () => OrderByField | undefined
}

const emit = defineEmits<{
	(e: 'switchToMatrixTab' | 'restoreTab'): void
	(e: 'setOverviewExpanded', value: boolean): void
}>()
const { t } = useI18n()
const props = defineProps<Props>()

const currentDate = computed(() => formatDate(new Date()))
const currentUserName = computed(() => sessionMap.user.label)
const currentInstanceName = computed(() => sessionMap.user.instances[0].name)

const catalogs = ref<RiskCatalogMeta[]>([])
const shouldRestoreOverview = ref(false)
const isTitleModalVisible = ref(false)
const isGenerating = ref(false)
const titleText = ref('')
const descriptionText = ref('')

watch(
	() => riskConfig.value.catalogs,
	() => (catalogs.value = catalogs.value.filter((c) => riskConfig.value.catalogs.some((rc) => rc.id === c.id))),
	{ immediate: true },
)

const options = {
	includeTitlePage: ref(true),
	includeOverview: ref(false),
	includeRiskMatrix: ref(false),
	includeImpactedAssets: ref(false),
	includeThreats: ref(true),
	includeVulnerabilities: ref(true),
	includeMitigationMeasures: ref(true),
	includeDescription: ref(false),
}

const checkboxOptions = Object.entries(options).map(([key, model]) => ({
	key,
	id: `${key}Check`,
	label: `pdf.${key}`,
	model,
}))

function getLevel(value: number | undefined, levels: any[]) {
	return value !== undefined && formatAxisLevel(value) < levels.length ? levels[formatAxisLevel(value)] : undefined
}

function getRiskLevel(probability?: number, impact?: number) {
	return getLevel(probability ?? impact, riskConfig.value.riskLevels) || { color: 'transparent' }
}

function drawLabelValue(page: PDFPage, x: number, y: number, label: string, value: string) {
	const labelWidth = drawText(page, x, y, label, 10, COLORS.BLACK, true)
	drawText(page, x + labelWidth + 3, y, value, 10, COLORS.GRAY)
	return labelWidth + getBodyFont().widthOfTextAtSize(value, 10) + 3
}

function drawIconLabelValue(page: PDFPage, x: number, y: number, icon: PDFImage, label: string, value: string) {
	page.drawImage(icon, { x, y: y - 2, width: 12, height: 12 })
	return 17 + drawLabelValue(page, x + 17, y, label, value)
}

function drawBadge(page: PDFPage, x: number, y: number, text: string, color: string) {
	const textWidth = getBodyFont().widthOfTextAtSize(sanitizeText(text), 9)
	const badgeWidth = textWidth + 8
	const rgbColor = hexToRgb(color)
	const darkerColor = rgb(rgbColor.red * 0.7, rgbColor.green * 0.7, rgbColor.blue * 0.7)

	page.drawRectangle({ x, y: y - 2, width: badgeWidth, height: 14, color: rgbColor, opacity: 0.7 })
	page.drawRectangle({ x, y: y - 2, width: badgeWidth, height: 14, borderColor: darkerColor, borderWidth: 0.5 })
	drawText(page, x + 4, y + 2, text, 9, COLORS.WHITE)
	return badgeWidth
}

function drawIconLabel(page: PDFPage, x: number, y: number, icon: PDFImage, text: string, iconSize = 12) {
	page.drawImage(icon, { x, y: y - iconSize / 2, width: iconSize, height: iconSize })
	drawText(page, x + iconSize + 8, y - 5, text, 10)
}

function createTitlePage(pdfDoc: PDFDocument, images: LoadedImages) {
	const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT])

	page.drawRectangle({ x: 0, y: A4_HEIGHT * 0.4, width: A4_WIDTH, height: A4_HEIGHT * 0.6, color: COLORS.LIGHT_RED })
	drawText(page, 70, A4_HEIGHT - TITLE_PAGE.TITLE_Y, titleText.value || t('pdf.riskReport'), TITLE_PAGE.TITLE_SIZE, COLORS.DARK_RED, true)
	page.drawLine({ start: { x: 70, y: A4_HEIGHT - 285 }, end: { x: 200, y: A4_HEIGHT - 285 }, thickness: 3, color: COLORS.RED })

	if (descriptionText.value) {
		wrapText(descriptionText.value, A4_WIDTH - 140, TITLE_PAGE.DESC_SIZE).forEach((line, i) =>
			drawText(page, 70, A4_HEIGHT - TITLE_PAGE.DESC_START_Y - i * TITLE_PAGE.DESC_LINE_HEIGHT, line, TITLE_PAGE.DESC_SIZE, COLORS.GRAY),
		)
	}

	if (catalogs.value.length > 0) {
		const catalogsX = 60
		let catalogsY = A4_HEIGHT - TITLE_PAGE.CATALOGS_START_Y
		const catalogWidth = A4_WIDTH - 120

		drawText(page, catalogsX, catalogsY, t('risk.catalog', 2), TITLE_PAGE.CATALOG_TITLE_SIZE, COLORS.DARK_RED, true)
		page.drawLine({
			start: { x: catalogsX, y: catalogsY - 5 },
			end: { x: catalogsX + 100, y: catalogsY - 5 },
			thickness: 2,
			color: COLORS.RED,
		})
		catalogsY -= 30

		catalogs.value.forEach((catalog) => {
			page.drawRectangle({ x: catalogsX, y: catalogsY - 5, width: catalogWidth, height: 20, color: COLORS.LIGHT_RED, opacity: 0.3 })
			drawText(page, catalogsX + 5, catalogsY, catalog.catalog.name, 12)

			const stats = [
				`${catalog.numRisks} ${t('risk.risk', catalog.numRisks === 1 ? 0 : 2)}`,
				`${catalog.numThreats} ${t('risk.threat', catalog.numThreats === 1 ? 0 : 2)}`,
				`${catalog.numVulnerabilities} ${t('risk.vulnerability', catalog.numVulnerabilities === 1 ? 0 : 2)}`,
			]

			let xOffset = catalogsX + catalogWidth
			stats.reverse().forEach((statText) => {
				const statWidth = getBodyFont().widthOfTextAtSize(statText, 11)
				xOffset -= statWidth + 15

				page.drawRectangle({
					x: xOffset,
					y: catalogsY - 5,
					width: statWidth + 10,
					height: 20,
					color: COLORS.RED,
				})
				drawText(page, xOffset + 5, catalogsY, statText, 11, COLORS.WHITE)
			})

			catalogsY -= 25
		})
	}

	;[
		{ icon: images.buildingPng, text: currentInstanceName.value },
		{ icon: images.userPng, text: currentUserName.value },
		{ icon: images.calendarPng, text: currentDate.value },
	].forEach((item, i) => {
		const y = A4_HEIGHT - TITLE_PAGE.META_START_Y - i * TITLE_PAGE.META_SPACING
		page.drawCircle({ x: 75, y, size: 12, color: COLORS.RED, opacity: 0.2 })
		drawIconLabel(page, 67, y, item.icon, item.text, 16)
	})

	const logoWidth = TITLE_PAGE.LOGO_HEIGHT * images.logoFullAspect
	page.drawImage(images.logoFullPng, {
		x: (A4_WIDTH - logoWidth) / 2,
		y: A4_HEIGHT - TITLE_PAGE.LOGO_Y,
		width: logoWidth,
		height: TITLE_PAGE.LOGO_HEIGHT,
	})
}

async function captureElementAsPng(
	pdfDoc: PDFDocument,
	element: HTMLElement,
	hideSelectors?: string[],
	removeClassSelectors?: Array<{ selector: string; className: string }>,
) {
	const changes: Array<{ el: HTMLElement; type: 'display' | 'class'; value: string }> = []

	hideSelectors?.forEach((selector) =>
		element.querySelectorAll(selector).forEach((el) => {
			const htmlEl = el as HTMLElement
			changes.push({ el: htmlEl, type: 'display', value: htmlEl.style.display })
			htmlEl.style.display = 'none'
		}),
	)

	removeClassSelectors?.forEach(({ selector, className }) =>
		element.querySelectorAll(selector).forEach((el) => {
			const htmlEl = el as HTMLElement
			if (htmlEl.classList.contains(className)) {
				htmlEl.classList.remove(className)
				changes.push({ el: htmlEl, type: 'class', value: className })
			}
		}),
	)

	await new Promise((resolve) => setTimeout(resolve, 50))
	const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })

	changes.forEach(({ el, type, value }) => (type === 'display' ? (el.style.display = value) : el.classList.add(value)))

	const base64 = canvas.toDataURL('image/png').split(',')[1]
	return pdfDoc.embedPng(Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)))
}

async function addImagePage(pdfDoc: PDFDocument, pageOffset: number, images: LoadedImages, title: string, element?: HTMLElement) {
	if (!element) return pageOffset

	const pngImage = await captureElementAsPng(
		pdfDoc,
		element,
		['.methods-overview-card'],
		[{ selector: '.rounded-container', className: 'bg-lightblue' }],
	)
	const scale = Math.min((A4_WIDTH - 80) / pngImage.width, (A4_HEIGHT - 120) / pngImage.height)

	const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT])
	drawModernHeader(page, images, title)
	page.drawImage(pngImage, {
		x: (A4_WIDTH - pngImage.width * scale) / 2,
		y: A4_HEIGHT - 100 - pngImage.height * scale,
		width: pngImage.width * scale,
		height: pngImage.height * scale,
	})

	return pageOffset + 1
}

function drawSection(ctx: DrawContext, title: string, content: () => void) {
	ctx = ensureSpace(ctx, 12)
	drawText(ctx.page, MARGIN, ctx.currentY, title, 10, COLORS.BLACK, true)
	ctx.currentY -= 12
	content()
	ctx.currentY -= 3
	return ctx
}

function drawListSection(ctx: DrawContext, items: any[], title: string, formatter: (item: any) => string) {
	if (!items?.length) return ctx
	return drawSection(ctx, title, () => {
		items.slice(0, 5).forEach((item) => {
			ctx = ensureSpace(ctx, 12)
			drawText(ctx.page, MARGIN + 10, ctx.currentY, formatter(item), 9, COLORS.GRAY)
			ctx.currentY -= 12
		})
	})
}

function drawMitigationLine(ctx: DrawContext, line: string, xStart: number, isFirstLine: boolean, measure: any) {
	let xPos = xStart

	if (isFirstLine && !measure.isApplicable) {
		const wniText = t('risk.mitigation.willNotImplement')
		drawText(ctx.page, xPos, ctx.currentY, wniText, 8, COLORS.DARK_RED)
		xPos += getBodyFont().widthOfTextAtSize(wniText, 8) + 8
	}

	if (isFirstLine) {
		ctx.page.drawCircle({ x: xPos, y: ctx.currentY + 3, size: 3, color: getStatusColor(measure.status) })
		xPos += 10
	}

	line.split('<<IMG>>').forEach((textPart, idx, arr) => {
		if (textPart) {
			const assetMatch = textPart.match(new RegExp(`(.*?)(${t('asset.label')}:)(.*)`))
			if (assetMatch) {
				const [, before, label, after] = assetMatch
				if (before) {
					drawText(ctx.page, xPos, ctx.currentY, before, 9, COLORS.GRAY)
					xPos += getBodyFont().widthOfTextAtSize(before, 9)
				}
				drawText(ctx.page, xPos, ctx.currentY, label, 9, COLORS.BLACK, true)
				xPos += getTitleFont().widthOfTextAtSize(label, 9) + 3
				if (after) {
					drawText(ctx.page, xPos, ctx.currentY, after, 9, COLORS.GRAY)
					xPos += getBodyFont().widthOfTextAtSize(after, 9)
				}
			} else {
				drawText(ctx.page, xPos, ctx.currentY, textPart, 9, COLORS.GRAY)
				xPos += getBodyFont().widthOfTextAtSize(textPart, 9)
			}
		}
		if (idx < arr.length - 1) {
			xPos += 8
			ctx.page.drawImage(ctx.images.userPng, { x: xPos, y: ctx.currentY - 2, width: 10, height: 10 })
			xPos += 15
		}
	})
}

function wrapMeasureText(measure: any): string[] {
	const wniW = !measure.isApplicable ? getBodyFont().widthOfTextAtSize(t('risk.mitigation.willNotImplement'), 8) + 8 : 0
	const widths = [A4_WIDTH - MARGIN - (MARGIN + 25 + wniW + 10), A4_WIDTH - MARGIN * 2 - 30]
	const parts = [
		`${measure.method.description} (${measure.method.name}) ${measure.price || 0}€`,
		...(measure.assetId && measure.assetName ? [`  ${t('asset.label')}:`, ` ${measure.assetName}`] : []),
		'<<IMG>>',
		measure.responsible?.label || '-',
	]

	const lines: string[] = []
	let line = ''
	let lineW = 0

	parts.forEach((part) => {
		if (part === '<<IMG>>') {
			line += part
			lineW += 33
			return
		}
		part.split(' ').forEach((word: string) => {
			if (!word) return
			const space = line && !line.endsWith(' ') && !line.endsWith('<<IMG>>') ? ' ' : ''
			const w = getBodyFont().widthOfTextAtSize(space + word, 9)
			if (lineW + w > widths[lines.length > 0 ? 1 : 0] && line) {
				lines.push(line)
				line = word
				lineW = getBodyFont().widthOfTextAtSize(word, 9)
			} else {
				line += space + word
				lineW += w
			}
		})
	})
	if (line) lines.push(line)
	return lines
}

function drawMitigationModule(ctx: DrawContext, summary: any, isFirst: boolean) {
	if (!isFirst) {
		ctx = ensureSpace(ctx, 8)
		drawSeparator(ctx.page, ctx.currentY + 5, 0.8)
		ctx.currentY -= 8
	}

	ctx = ensureSpace(ctx, 14)
	const isTotal = summary.moduleName === 'total'
	const name = isTotal ? t('risk.summary') : summary.moduleName
	drawText(ctx.page, MARGIN + 20, ctx.currentY, name, isTotal ? 11 : 10, COLORS.BLACK, true)

	let x = MARGIN + 20 + getTitleFont().widthOfTextAtSize(name, isTotal ? 11 : 10) + 10
	;[
		[t('method.label', 2), `${summary.applicableCount} / ${summary.mitigationMeasures.length}`],
		[t('risk.price'), `${summary.price} €`],
	].forEach(([label, value]) => {
		drawText(ctx.page, x, ctx.currentY, `${label}:`, 9, COLORS.BLACK, true)
		x += getTitleFont().widthOfTextAtSize(`${label}:`, 9) + 3
		drawText(ctx.page, x, ctx.currentY, value, 9, COLORS.GRAY)
		x += getBodyFont().widthOfTextAtSize(value, 9) + 10
	})

	if (summary.moduleStatuses) {
		drawText(ctx.page, x, ctx.currentY, `${t('method.status', 2)}:`, 9, COLORS.BLACK, true)
		x += getTitleFont().widthOfTextAtSize(`${t('method.status', 2)}:`, 9) + 5

		const s = summary.moduleStatuses
		;[
			{ c: COLORS.GREEN, p: s.i.percentage + s.ci.percentage },
			{ c: COLORS.ORANGE, p: s.pi.percentage },
			{ c: COLORS.RED, p: s.ni.percentage },
			{ c: COLORS.BLUE, p: s.na.percentage },
			{ c: COLORS.GRAY, p: s.wni.percentage },
		]
			.filter((st) => st.p > 0)
			.forEach(({ c, p }) => {
				ctx.page.drawCircle({ x, y: ctx.currentY + 3, size: 3, color: c })
				const pct = `${p.toFixed(1)}%`
				drawText(ctx.page, x + 6, ctx.currentY, pct, 9, COLORS.GRAY)
				x += 6 + getBodyFont().widthOfTextAtSize(pct, 9) + 8
			})
	}

	ctx.currentY -= 14

	if (options.includeMitigationMeasures.value && !isTotal) {
		summary.mitigationMeasures.forEach((m: any) => {
			wrapMeasureText(m).forEach((ln, idx) => {
				ctx = ensureSpace(ctx, SPACING.line)
				drawMitigationLine(ctx, ln, idx === 0 ? MARGIN + 25 : MARGIN + 30, idx === 0, m)
				ctx.currentY -= SPACING.line
			})
		})
	}

	return ctx
}

function drawRiskMeta(ctx: DrawContext, riskMeta: RiskMeta, risk: Risk): DrawContext {
	const isDefaultRisk = !riskMeta.asset.id

	ctx = ensureSpace(ctx, SPACING.section)
	drawText(
		ctx.page,
		MARGIN,
		ctx.currentY,
		`[${risk.code}] ${risk.name} (${isDefaultRisk ? t('risk.default') : riskMeta.asset.name})`,
		12,
		isDefaultRisk ? COLORS.DARK_RED : COLORS.BLUE,
		true,
	)
	ctx.currentY -= SPACING.block + 2
	;[
		() => {
			ctx = ensureSpace(ctx, SPACING.section)
			drawIconLabelValue(
				ctx.page,
				MARGIN,
				ctx.currentY,
				ctx.images.userPng,
				`${t('risk.responsible')}:`,
				riskMeta.responsible?.label || '-',
			)
			ctx.currentY -= SPACING.section
		},
		() => {
			ctx = ensureSpace(ctx, SPACING.section)
			let x = MARGIN
			x +=
				drawIconLabelValue(
					ctx.page,
					x,
					ctx.currentY,
					ctx.images.calendarPng,
					`${t('risk.dueDate')}:`,
					riskMeta.dueDate ? formatDate(riskMeta.dueDate) : '-',
				) + 8
			drawText(ctx.page, x, ctx.currentY, '|', 10, COLORS.LIGHT_GRAY)
			x += 8
			drawLabelValue(ctx.page, x, ctx.currentY, `${t('risk.lastReview')}:`, riskMeta.lastReview ? formatDate(riskMeta.lastReview) : '-')
			ctx.currentY -= SPACING.section
		},
		() => {
			ctx = ensureSpace(ctx, SPACING.section)
			let x = MARGIN
			x += drawLabelValue(ctx.page, x, ctx.currentY, `${t('risk.catalog', 1)}:`, riskMeta.catalogMeta.catalog.name) + 8
			drawText(ctx.page, x, ctx.currentY, '|', 10, COLORS.LIGHT_GRAY)
			x += 8
			drawLabelValue(ctx.page, x, ctx.currentY, `${t('risk.category', 1)}:`, riskMeta.category?.name || '-')
			ctx.currentY -= SPACING.section
		},
	].forEach((fn) => fn())

	ctx = ensureSpace(ctx, SPACING.section)
	let x = MARGIN
	const drawScoreBadge = (label: string, score: number) => {
		drawText(ctx.page, x, ctx.currentY, `${label}:`, 10, COLORS.BLACK, true)
		x += getTitleFont().widthOfTextAtSize(`${label}:`, 10) + 5
		const level = scoreToLevel(score)
		if (level) x += drawBadge(ctx.page, x, ctx.currentY, `${formatRiskScoreStr(score)} ${level.title}`, level.color) + 10
	}
	drawScoreBadge(t('risk.score.label'), riskMeta.score)
	drawScoreBadge(t('risk.residual'), riskMeta.residual)
	ctx.currentY -= SPACING.section

	ctx = ensureSpace(ctx, SPACING.section)
	drawLabelValue(ctx.page, MARGIN, ctx.currentY, `${t('risk.riskTreatment')}:`, getTreatmentText(riskMeta.treatment))
	ctx.currentY -= SPACING.section

	ctx = ensureSpace(ctx, SPACING.section)
	drawLabelValue(
		ctx.page,
		MARGIN,
		ctx.currentY,
		`${t('risk.acceptable')}:`,
		formatRiskScore(riskMeta.acceptedResidualRisk || riskConfig.value.acceptableRisk).toFixed(0),
	)
	ctx.currentY -= SPACING.block

	ctx = ensureSpace(ctx, SPACING.block)
	x = MARGIN
	const drawRiskAxis = (label: string, value: number | undefined, levels: any[]) => {
		const level = getLevel(value, levels)
		const riskLevel = getRiskLevel(value)
		drawText(ctx.page, x, ctx.currentY, `${label}:`, 10, COLORS.BLACK, true)
		x += getTitleFont().widthOfTextAtSize(`${label}:`, 10) + 5
		if (riskLevel?.color && level?.title && value !== undefined) {
			ctx.page.drawCircle({ x, y: ctx.currentY + 4, size: 3, color: hexToRgb(riskLevel.color) })
			const text = `${formatAxisLevel(value) + 1} ${level.title}`
			drawText(ctx.page, x + 8, ctx.currentY, text, 10, COLORS.GRAY)
			x += 8 + getBodyFont().widthOfTextAtSize(text, 10) + 15
		}
	}
	drawRiskAxis(t('risk.probability'), riskMeta.probability, riskConfig.value.riskProbabilities)
	drawRiskAxis(t('risk.impact'), riskMeta.impact, riskConfig.value.riskImpacts)
	ctx.currentY -= SPACING.block

	if (options.includeDescription.value && risk.description) {
		ctx = drawSection(ctx, t('risk.description'), () => {
			wrapText(risk.description!, A4_WIDTH - MARGIN * 2 - 20, 9).forEach((line) => {
				ctx = ensureSpace(ctx, SPACING.line)
				drawText(ctx.page, MARGIN + 10, ctx.currentY, line, 9, COLORS.GRAY)
				ctx.currentY -= SPACING.line
			})
		})
	}

	if (options.includeImpactedAssets.value)
		ctx = drawListSection(ctx, riskMeta.impactedAssets || [], t('risk.impactedAsset', 2), (a) => `• ${a.name}`)
	if (options.includeThreats.value)
		ctx = drawListSection(ctx, riskMeta.threats || [], t('risk.threat', 2), (t) => `• ${t.threat.name} (${t.threat.code})`)
	if (options.includeVulnerabilities.value)
		ctx = drawListSection(
			ctx,
			riskMeta.vulnerabilities || [],
			t('risk.vulnerability', 2),
			(v) => `• ${v.vulnerability.name} (${v.vulnerability.code})`,
		)

	if (riskMeta.mitigationSummary?.length) {
		ctx = drawSection(ctx, t('risk.mitigationMeasure', 2), () => {
			riskMeta.mitigationSummary!.forEach((summary, i) => {
				ctx = drawMitigationModule(ctx, summary, i === 0)
			})
		})
	}

	ctx = ensureSpace(ctx, SPACING.separator)
	drawSeparator(ctx.page, ctx.currentY)
	ctx.currentY -= SPACING.separator

	return ctx
}

async function generatePdf() {
	const request: PageRequest<RiskFilter> = { page: 1, limit: 999999, filter: props.filterValue(), orderBy: props.orderValue() }
	const { data } = await http.post<Response<PageResponse<Risk>>>('api/risk/list', request)
	const risks = data.data?.items || []

	const pdfDoc = await PDFDocument.create()
	await initializeFonts(pdfDoc)
	const images = await loadAllImages(pdfDoc)

	let pageOffset = 0

	if (options.includeTitlePage.value) {
		createTitlePage(pdfDoc, images)
		pageOffset = 1
	}

	if (options.includeOverview.value && props.overviewRef) {
		shouldRestoreOverview.value = !props.overviewRef.expanded
		if (shouldRestoreOverview.value) {
			emit('setOverviewExpanded', true)
			await nextTick()
		}
		pageOffset = await addImagePage(pdfDoc, pageOffset, images, titleText.value, props.overviewSection)
	}

	if (options.includeRiskMatrix.value) {
		emit('switchToMatrixTab')
		await nextTick()

		const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT])
		drawModernHeader(page, images, titleText.value)
		let currentY = A4_HEIGHT - 100

		for (const element of [props.riskScoreSection, props.matrixSection].filter(Boolean) as HTMLElement[]) {
			const pngImage = await captureElementAsPng(pdfDoc, element)
			const scale = Math.min((A4_WIDTH - 80) / pngImage.width, (currentY - 80) / pngImage.height)
			const [drawWidth, drawHeight] = [pngImage.width * scale, pngImage.height * scale]
			currentY -= drawHeight
			page.drawImage(pngImage, { x: (A4_WIDTH - drawWidth) / 2, y: currentY, width: drawWidth, height: drawHeight })
			currentY -= 20
		}
		pageOffset += 1
	}

	let ctx: DrawContext = {
		pdfDoc,
		page: pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]),
		currentY: CONTENT_START_Y,
		currentPageNum: pageOffset,
		images,
		titleText: titleText.value,
	}

	drawModernHeader(ctx.page, images, titleText.value)
	risks.forEach((risk) => risk.riskMetas.forEach((riskMeta) => (ctx = drawRiskMeta(ctx, riskMeta, risk))))

	if (shouldRestoreOverview.value) emit('setOverviewExpanded', false)
	if (options.includeRiskMatrix.value) emit('restoreTab')

	pdfDoc.getPages().forEach((page, index) => {
		if (index > 0) drawFooter(page, index - 1, pdfDoc.getPageCount() - 1, images)
	})

	const finalPdf = await pdfDoc.save()
	const blob = new Blob([finalPdf.buffer as ArrayBuffer], { type: 'application/pdf' })
	const url = URL.createObjectURL(blob)
	const link = Object.assign(document.createElement('a'), { href: url, download: 'risks.pdf' })
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

function openModal() {
	isTitleModalVisible.value = true
}

async function confirmPdfExport() {
	isGenerating.value = true
	await generatePdf()
	isGenerating.value = false
	isTitleModalVisible.value = false
}

defineExpose({ openModal })

onBeforeMount(async () => {
	if (hasPermission(PermOwnRiskRead, PermAllRiskRead)) {
		const { data } = await http.get<Response<RiskCatalogMeta[]>>('/api/risk/catalogs')
		if (data.data) catalogs.value = data.data
	}
})
</script>

<template>
	<Modal v-model="isTitleModalVisible" :hide-footer="isGenerating">
		<template #header>{{ $t('pdf.export') }}</template>

		<template #body>
			<div v-if="!isGenerating">
				<div class="mb-3">
					<label class="form-label">{{ $t('pdf.title') }}</label>
					<input v-model="titleText" class="form-control" :maxlength="30" />
				</div>

				<div class="mb-3">
					<label class="form-label">{{ $t('pdf.description') }}</label>
					<textarea v-model="descriptionText" class="form-control" rows="3" :maxlength="350" />
				</div>

				<div class="mb-3 fw-bold">
					<label class="form-label">{{ $t('pdf.includeInRaport') }}</label>
				</div>

				<div v-for="opt in checkboxOptions" :key="opt.key" class="mb-3 form-check">
					<input type="checkbox" class="form-check-input" :id="opt.id" v-model="opt.model.value" />
					<label class="form-check-label" :for="opt.id">{{ $t(opt.label) }}</label>
				</div>
			</div>

			<div v-else class="text-center py-5">
				<div class="spinner-border text-primary mb-3" role="status">
					<span class="visually-hidden">{{ $t('misc.loading') }}</span>
				</div>
				<p class="text-muted">{{ $t('pdf.generate', 2) }}</p>
			</div>
		</template>

		<template #footer>
			<button v-if="!isGenerating" class="btn btn-primary" @click="confirmPdfExport">{{ $t('pdf.generate') }}</button>
		</template>
	</Modal>
</template>
